import { useEffect, useRef } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { LAYERS, type LayerKey, type BasemapKey } from './LayerControl'
import { fetchSitgLayer, type SitgLayerId } from '../lib/sitg'

// PFP charge deux sources SITG et les fusionne côté client
type SitgSource = SitgLayerId | SitgLayerId[]
const LAYER_TO_SITG: Record<LayerKey, SitgSource> = {
  point_limite: 'CAD_POINT_LIMITE',
  pfp: ['CAD_POINTFIXE_TRI', 'CAD_POINTFIXE_PP'], // cat. 1&2 + cat. 3 fusionnés
  pfa: 'CAD_POINTFIXE_NIV',
  parcelle: 'CAD_PARCELLE_MENSU',
}

const MIN_ZOOM_LABELS = 17

// Zoom minimal de chargement par couche
const LAYER_MIN_ZOOM: Record<LayerKey, number> = {
  point_limite: 15,
  pfp: 13,   // réseau sparse → visible plus tôt
  pfa: 13,
  parcelle: 15,
}
const SITG_RASTER_BASE = 'https://raster.sitg.ge.ch/arcgis/rest/services'

const BASEMAP_SOURCES: Record<BasemapKey, maplibregl.RasterSourceSpecification> = {
  osm: {
    type: 'raster',
    tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
    tileSize: 256,
    attribution: '© OpenStreetMap contributors',
    maxzoom: 19,
  },
  plan_sitg: {
    type: 'raster',
    tiles: [
      `${SITG_RASTER_BASE}/PLAN_SITG_EPSG2056/MapServer/export?bbox={bbox-epsg-3857}&bboxSR=3857&imageSR=3857&size=512,512&format=png&transparent=false&f=image`,
    ],
    tileSize: 512,
    attribution: '© SITG / République et canton de Genève',
    maxzoom: 21,
  },
  ortho_2024: {
    type: 'raster',
    tiles: [`${SITG_RASTER_BASE}/ORTHOPHOTOS_2024_EPSG3857/MapServer/tile/{z}/{y}/{x}`],
    tileSize: 256,
    attribution: '© SITG — Orthophoto mars 2024',
    maxzoom: 22,
    scheme: 'xyz',
  },
}

interface Props {
  activeLayers: Set<LayerKey>
  basemap: BasemapKey
  showLabels: boolean
}

// Champs masqués dans les popups
const HIDDEN_FIELDS = new Set([
  'objectid',
  'globalid',
  'mutnum',
  'mutori',
  'mutmai',
  'x',
  'y',
  'z',
  'ideddp',
  'code_propri_publique_rf',
  'code_immeuble_rf',
  'plan_rf',
  'lien_www',
  'extrait_rdppf_pdf',
  'SHAPE__Length',
  'SHAPE__Area',
])

const FIELD_LABELS: Record<string, string> = {
  no_point: 'N° point',
  numero: 'N° point',
  no_plan: 'N° plan',
  no_prov: 'N° provisoire',
  no_comm: 'N° commune',
  nature: 'Nature',
  valeur: 'Valeur',
  precplan: 'Précision plan (cm)',
  precalt: 'Précision alt. (cm)',
  fiabplan: 'Fiabilité plan',
  fiabalt: 'Fiabilité alt.',
  datedt: 'Date',
  provenance: 'Provenance',
  categorie: 'Catégorie',
  genre: 'Genre',
  presence: 'Présence',
  accessibilite: 'Accessibilité',
  regard: 'Regard',
  nocart: 'N° carte',
  inscri: 'Inscription',
  rep_scelle: 'Repère scellé',
  rep_acces: 'Repère accès',
  mes_visibilite_gps: 'Visibilité GPS',
  reg_profondeur: 'Prof. regards (cm)',
  no_parcelle: 'N° parcelle',
  commune: 'Commune',
  egrid: 'E-GRID (identifiant fédéral)',
  surface: 'Surface (m²)',
  type_propri: 'Type de propriété',
  propri_publique: 'Propriétaire public',
  validite: 'Validité',
}

const LAYER_LABELS: Record<string, string> = {
  'lyr-point_limite': 'Point limite',
  'lyr-pfp': 'Point fixe planimétrique',
  'lyr-pfa': 'Point fixe altimétrique',
  'lyr-parcelle': 'Parcelle cadastrale',
}

function formatValue(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (key === 'datedt' && typeof value === 'number') {
    const d = new Date(value)
    if (!isNaN(d.getTime())) return d.toLocaleDateString('fr-CH')
  }
  return String(value)
}

function buildPopupHtml(layerId: string, props: Record<string, unknown>): string {
  const title = LAYER_LABELS[layerId] ?? layerId
  const rows = Object.entries(props)
    .filter(([k, v]) => !HIDDEN_FIELDS.has(k) && v !== null && v !== '')
    .map(([k, v]) => {
      const label = FIELD_LABELS[k] ?? k
      return `<tr><td class="popup-key">${label}</td><td class="popup-val">${formatValue(k, v)}</td></tr>`
    })
    .join('')
  return `<div class="popup-title">${title}</div><table class="popup-table">${rows}</table>`
}

// Styling conditionnel points limites (valeur = 6 → rouge)
const PL_CIRCLE_COLOR: maplibregl.ExpressionSpecification = [
  'case',
  ['==', ['get', 'valeur'], 6],
  '#dc2626',
  '#4b5563',
]
const PL_LABEL_COLOR: maplibregl.ExpressionSpecification = [
  'case',
  ['==', ['get', 'valeur'], 6],
  '#dc2626',
  '#111827',
]

// Étiquette PFP : coalesce entre 'numero' (TRI cat.1&2) et 'no_point' (PP cat.3)
const PFP_TEXT_FIELD: maplibregl.ExpressionSpecification = [
  'to-string',
  ['coalesce', ['get', 'numero'], ['get', 'no_point'], ''],
]

// Points limites : rayon × 0.8 (−20 % vs original)
const CIRCLE_RADIUS_SM: maplibregl.ExpressionSpecification = [
  'interpolate', ['linear'], ['zoom'],
  14, 1.6,
  18, 4.8,
]
// PFP & PFA : rayon × 0.96 (−20 % puis +20 %), visible dès zoom 13
// Pente = (5.76 − 1.92) / (18 − 14) = 0.96 px/niveau → zoom 13 : 1.92 − 0.96 = 0.96 px
const CIRCLE_RADIUS_LG: maplibregl.ExpressionSpecification = [
  'interpolate', ['linear'], ['zoom'],
  13, 0.96,
  18, 5.76,
]

export default function MapView({ activeLayers, basemap, showLabels }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const activeLayersRef = useRef(activeLayers)

  useEffect(() => {
    activeLayersRef.current = activeLayers
  }, [activeLayers])

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
        sources: { basemap: BASEMAP_SOURCES[basemap] },
        layers: [{ id: 'basemap', type: 'raster', source: 'basemap' }],
      },
      center: [6.1432, 46.2044],
      zoom: 12,
      maxZoom: 22,
    })

    map.addControl(new maplibregl.NavigationControl(), 'top-right')
    map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left')
    map.addControl(
      new maplibregl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
      }),
      'top-right',
    )

    map.on('load', () => {
      const allKeys = Object.keys(LAYERS) as LayerKey[]
      const polygonKeys = allKeys.filter((k) => LAYERS[k].type === 'polygon')
      const pointKeys = allKeys.filter((k) => LAYERS[k].type === 'point')

      // 1. Sources
      allKeys.forEach((key) => {
        map.addSource(`src-${key}`, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        })
      })

      // 2. Polygones (sous les points)
      polygonKeys.forEach((key) => {
        map.addLayer({
          id: `lyr-${key}`,
          type: 'fill',
          source: `src-${key}`,
          paint: { 'fill-color': LAYERS[key].color, 'fill-opacity': 0.12 },
          layout: { visibility: 'none' },
        })
        map.addLayer({
          id: `outline-${key}`,
          type: 'line',
          source: `src-${key}`,
          paint: { 'line-color': LAYERS[key].color, 'line-width': 1.5 },
          layout: { visibility: 'none' },
        })
      })

      // 3. Points (cercles)
      pointKeys.forEach((key) => {
        map.addLayer({
          id: `lyr-${key}`,
          type: 'circle',
          source: `src-${key}`,
          paint: {
            'circle-radius': (key === 'pfp' || key === 'pfa') ? CIRCLE_RADIUS_LG : CIRCLE_RADIUS_SM,
            'circle-color': key === 'point_limite' ? PL_CIRCLE_COLOR : LAYERS[key].color,
            'circle-stroke-width': 1,
            'circle-stroke-color': '#ffffff',
          },
          layout: { visibility: 'none' },
        })
      })

      // 4. Étiquettes
      allKeys.forEach((key) => {
        const textField: maplibregl.ExpressionSpecification =
          key === 'pfp'
            ? PFP_TEXT_FIELD
            : ['to-string', ['get', LAYERS[key].labelField]]

        map.addLayer({
          id: `lbl-${key}`,
          type: 'symbol',
          source: `src-${key}`,
          minzoom: MIN_ZOOM_LABELS,
          layout: {
            'text-field': textField,
            'text-size': 11,
            'text-offset': [0, 1.0],
            'text-anchor': 'top',
            'text-allow-overlap': false,
            'text-optional': true,
            visibility: 'none',
          },
          paint: {
            'text-color': key === 'point_limite' ? PL_LABEL_COLOR : LAYERS[key].color,
            'text-halo-color': '#ffffff',
            'text-halo-width': 1.5,
          },
        })
      })

      // Popup au clic (fonctionne sur fill et circle)
      map.on('click', (e) => {
        const clickLayerIds = allKeys.map((k) => `lyr-${k}`)
        const features = map.queryRenderedFeatures(e.point, { layers: clickLayerIds })
        if (!features.length) return
        const f = features[0]
        const props = (f.properties ?? {}) as Record<string, unknown>
        const html = buildPopupHtml(f.layer.id, props)
        new maplibregl.Popup({ maxWidth: '320px' })
          .setLngLat(e.lngLat)
          .setHTML(html)
          .addTo(map)
      })

      // Curseur pointer au survol
      allKeys.forEach((key) => {
        map.on('mouseenter', `lyr-${key}`, () => (map.getCanvas().style.cursor = 'pointer'))
        map.on('mouseleave', `lyr-${key}`, () => (map.getCanvas().style.cursor = ''))
      })

      const refresh = () => loadVisibleData(map)
      map.on('moveend', refresh)
      map.on('zoomend', refresh)
      refresh()
    })

    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Switch basemap dynamiquement
  useEffect(() => {
    const map = mapRef.current
    if (!map || !map.isStyleLoaded()) return
    if (map.getLayer('basemap')) map.removeLayer('basemap')
    if (map.getSource('basemap')) map.removeSource('basemap')
    map.addSource('basemap', BASEMAP_SOURCES[basemap])
    const allKeys = Object.keys(LAYERS) as LayerKey[]
    const bottomLayer = [
      ...allKeys.filter((k) => LAYERS[k].type === 'polygon').map((k) => `lyr-${k}`),
      ...allKeys.filter((k) => LAYERS[k].type === 'point').map((k) => `lyr-${k}`),
    ].find((id) => map.getLayer(id))
    map.addLayer({ id: 'basemap', type: 'raster', source: 'basemap' }, bottomLayer)
  }, [basemap])

  // Toggle visibilité
  useEffect(() => {
    const map = mapRef.current
    if (!map || !map.isStyleLoaded()) return
    ;(Object.keys(LAYERS) as LayerKey[]).forEach((key) => {
      const visible = activeLayers.has(key) ? 'visible' : 'none'
      const labelVisible = activeLayers.has(key) && showLabels ? 'visible' : 'none'
      if (map.getLayer(`lyr-${key}`)) map.setLayoutProperty(`lyr-${key}`, 'visibility', visible)
      if (map.getLayer(`outline-${key}`)) map.setLayoutProperty(`outline-${key}`, 'visibility', visible)
      if (map.getLayer(`lbl-${key}`)) map.setLayoutProperty(`lbl-${key}`, 'visibility', labelVisible)
    })
    loadVisibleData(map)
  }, [activeLayers, showLabels])

  async function loadVisibleData(map: maplibregl.Map) {
    const zoom = map.getZoom()
    const bounds = map.getBounds()
    const bbox = {
      xmin: bounds.getWest(),
      ymin: bounds.getSouth(),
      xmax: bounds.getEast(),
      ymax: bounds.getNorth(),
    }

    const active = activeLayersRef.current
    await Promise.all(
      (Object.keys(LAYERS) as LayerKey[]).map(async (key) => {
        const src = map.getSource(`src-${key}`) as maplibregl.GeoJSONSource | undefined
        if (!src) return

        // Vider la source si zoom insuffisant pour cette couche
        if (zoom < LAYER_MIN_ZOOM[key]) {
          src.setData({ type: 'FeatureCollection', features: [] })
          return
        }
        if (!active.has(key)) return

        try {
          const sources = Array.isArray(LAYER_TO_SITG[key])
            ? (LAYER_TO_SITG[key] as SitgLayerId[])
            : [LAYER_TO_SITG[key] as SitgLayerId]
          const results = await Promise.all(sources.map((id) => fetchSitgLayer(id, bbox)))
          src.setData({
            type: 'FeatureCollection',
            features: results.flatMap((r) => r.features),
          })
        } catch (err) {
          console.error(`Erreur chargement ${key}`, err)
        }
      }),
    )
  }

  return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
}
